import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Sidebar } from '../modules/Sidebar';
import { swiperNavigation, swiperProductGalleryPagination, swiperProductGalleryThumbnailsNavigation } from '../modules/swiperModules';

const SLIDE_CLASSES = {
    product:
        'swiper-slide bg-background-secondary aspect-square h-full w-full group isolate',
    default:
        'swiper-slide bg-background-secondary aspect-[4/3] group isolate [&:not(.swiper-slide-active)>img]:ml-1',
};

const MAIN_IMAGE_CLASSES = {
    product: 'rounded-medium w-full h-full mix-blend-multiply object-cover',
    default: 'rounded-medium w-full h-full mix-blend-multiply object-contain',
};

const LIGHTBOX_IMAGE_CLASSES =
    'h-full w-full object-contain origin-[0_0] cursor-zoom-in [&.-zoommed]:cursor-zoom-out select-none transition-all mix-blend-multiply';

const THUMB_IMAGE_CLASSES = 'size-full object-cover mix-blend-multiply';

const SIDEBAR_THUMB_SLIDE_CLASSES =
    'swiper-slide !size-[72px] bg-background-secondary rounded-medium overflow-hidden shrink-0 cursor-pointer border border-transparent transition-colors duration-200 hover:border-border-tertiary [&.swiper-slide-thumb-active]:border-border-accent isolate';

const LIGHTBOX_THUMB_SLIDE_CLASSES =
    'swiper-slide desktop:w-[120px]! bg-background-secondary aspect-square group rounded-[1.8px] border-t border-t-transparent [&.swiper-slide-thumb-active]:border-t-border-accent transition-all cursor-pointer isolate';

const GALLERY_UPDATE_FADE_MS = 150;
const GALLERY_OPACITY_TRANSITION = `opacity ${GALLERY_UPDATE_FADE_MS}ms ease-in-out`;

const prefersReducedGalleryMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const fadeGalleryOpacity = (element, opacity) =>
    new Promise(resolve => {
        if (!element) {
            resolve();
            return;
        }

        if (prefersReducedGalleryMotion()) {
            element.style.opacity = String(opacity);
            resolve();
            return;
        }

        let settled = false;
        const finish = () => {
            if (settled) {
                return;
            }

            settled = true;
            element.removeEventListener('transitionend', onTransitionEnd);
            clearTimeout(fallbackTimer);
            resolve();
        };

        const onTransitionEnd = event => {
            if (
                event.target === element &&
                event.propertyName === 'opacity'
            ) {
                finish();
            }
        };

        const fallbackTimer = setTimeout(
            finish,
            GALLERY_UPDATE_FADE_MS + 50
        );

        element.addEventListener('transitionend', onTransitionEnd);
        element.style.transition = GALLERY_OPACITY_TRANSITION;
        element.style.opacity = String(opacity);
    });

const preloadGalleryImages = images =>
    Promise.all(
        images.map(
            image =>
                new Promise(resolve => {
                    const src = String(image?.src || '').trim();

                    if (src === '') {
                        resolve();
                        return;
                    }

                    const img = new Image();
                    const done = () => resolve();

                    img.onload = done;
                    img.onerror = done;

                    if (image.srcset) {
                        img.srcset = image.srcset;
                    }

                    img.src = src;

                    if (img.complete) {
                        done();
                    }
                })
        )
    );

const createImageElement = (image, className) => {
    const img = document.createElement('img');
    img.src = image.src;
    img.alt = image.alt || '';
    img.className = className;

    if (image.srcset) {
        img.srcset = image.srcset;
    }

    return img;
};

const createSlide = (image, slideClass, imgClass) => {
    const slide = document.createElement('div');
    slide.className = slideClass;
    slide.appendChild(createImageElement(image, imgClass));

    return slide;
};

const createThumbSlide = (image, slideClass, isActive = false) => {
    const slide = document.createElement('div');
    slide.className = isActive
        ? `${slideClass} swiper-slide-thumb-active`
        : slideClass;
    slide.appendChild(
        createImageElement(
            {
                ...image,
                src: image.thumb_src || image.src,
                srcset: '',
            },
            THUMB_IMAGE_CLASSES
        )
    );

    return slide;
};

class Gallery {
    constructor(el) {
        if (!el) {
            return;
        }

        this.root = el;
        this.init();
    }

    init() {
        this.rootParent =
            this.root.closest('.woocommerce-product-gallery') ||
            this.root.parentElement;
        this.lightboxId = this.rootParent?.dataset.galleryLightboxId || '';
        this.context =
            this.rootParent?.dataset.context === 'product'
                ? 'product'
                : 'default';
        this.lightboxButton = this.root.querySelector('[js-lightbox-button]');
        this.lighboxEl =
            this.rootParent?.querySelector('[js-lightbox]') ||
            (this.lightboxId
                ? document.querySelector(
                      `[js-lightbox][data-gallery-lightbox-id="${this.lightboxId}"]`
                  )
                : null);
        this.closeLightboxButton =
            this.lighboxEl?.querySelector('[js-close-lighbox]');
        this.lighboxThumbnails = this.lighboxEl?.querySelector(
            '.lightbox-thumbnails'
        );
        this.lightboxGallery =
            this.lighboxEl?.querySelector('.lightbox-gallery');
        this.lightboxGalleryImgs =
            this.lightboxGallery?.querySelectorAll('img') || [];
        this.lightboxGalleryArrows =
            this.lighboxEl?.querySelector('.slider-arrows');
        this.arrowsEl = this.root.querySelector('.slider-arrows');
        this.arrowsWrapper = this.root.querySelector('[js-product-gallery-arrows]');
        this.mainWrapper = this.root.querySelector('.swiper-wrapper');
        this.lightboxWrapper =
            this.lightboxGallery?.querySelector('.swiper-wrapper');
        this.thumbnailsWrapper =
            this.lighboxThumbnails?.querySelector('.swiper-wrapper');
        this.sidebarThumbnailsEl = this.rootParent?.querySelector(
            '[js-product-gallery-thumbnails]'
        );
        this.sidebarThumbnailsArrowsEl = this.sidebarThumbnailsEl?.querySelector(
            '[js-product-gallery-thumbnails-arrows]'
        );
        this.sidebarThumbnailsSwiperEl = this.sidebarThumbnailsEl?.querySelector(
            '.product-gallery-thumbnails__swiper'
        );
        this.sidebarThumbnailsWrapper =
            this.sidebarThumbnailsSwiperEl?.querySelector('.swiper-wrapper');
        this.paginationEl = this.rootParent?.querySelector(
            '[js-product-gallery-pagination]'
        );

        this.swiperInstance = null;
        this.sidebarThumbnailsSwiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
        this.lightboxActiveClass = '-active';
        this._imagesUpdateToken = 0;
        this.onLightboxImgClickBound = this.onLightboxImgClick.bind(this);
        this.onLightboxKeydownsBound = this.onLightboxKeydowns.bind(this);
        this.onThumbnailHeightSyncBound =
            this.syncSidebarThumbnailsHeight.bind(this);

        this.mountLightboxToBody();
        this.initSidebarThumbnails();
        this.initLightbox();
        this.initSwiper();
        this.events();
        this.bindThumbnailHeightSync();

        if (this.rootParent) {
            this.rootParent.classList.add('-ready');
            this.rootParent.classList.remove('-is-pending');
        }
    }

    syncSidebarThumbnailsHeight() {
        if (!this.sidebarThumbnailsEl || !this.root) {
            return;
        }

        if (window.innerWidth < 1024) {
            this.sidebarThumbnailsEl.style.height = '';
            this.sidebarThumbnailsEl.style.maxHeight = '';
            this.syncThumbnailTrackHeight(false);
            return;
        }

        const slideCount =
            this.sidebarThumbnailsWrapper?.children.length || 0;
        const mainHeight = this.root.offsetHeight;

        if (mainHeight <= 0) {
            return;
        }

        if (slideCount <= 1) {
            this.sidebarThumbnailsEl.style.height = '';
            this.sidebarThumbnailsEl.style.maxHeight = '';
            this.syncThumbnailTrackHeight(false);
            this.sidebarThumbnailsSwiperInstance?.update();
            this.syncThumbnailArrowsVisibility();
            return;
        }

        this.sidebarThumbnailsEl.style.height = `${mainHeight}px`;
        this.sidebarThumbnailsEl.style.maxHeight = `${mainHeight}px`;
        this.syncThumbnailTrackHeight(true);
        this.sidebarThumbnailsSwiperInstance?.update();
        this.syncThumbnailArrowsVisibility();
    }

    syncThumbnailTrackHeight(useFullHeight) {
        const track = this.sidebarThumbnailsEl?.firstElementChild;
        const swiperEl = this.sidebarThumbnailsSwiperEl;

        if (!track || !swiperEl) {
            return;
        }

        track.classList.toggle('h-full', useFullHeight);
        track.classList.toggle('h-auto', !useFullHeight);
        swiperEl.classList.toggle('h-full', useFullHeight);
        swiperEl.classList.toggle('h-auto', !useFullHeight);
    }

    syncThumbnailArrowsVisibility() {
        const arrowsEl = this.sidebarThumbnailsArrowsEl;

        if (!arrowsEl || window.innerWidth < 1024) {
            arrowsEl?.classList.remove('is-active');
            return;
        }

        const swiper = this.sidebarThumbnailsSwiperInstance;
        const slideCount =
            this.sidebarThumbnailsWrapper?.children.length || 0;

        if (!swiper || slideCount <= 1) {
            arrowsEl.classList.remove('is-active');
            return;
        }

        arrowsEl.classList.toggle('is-active', !swiper.isLocked);
    }

    syncSingleThumbnailActiveState() {
        const slides = this.sidebarThumbnailsWrapper?.children;

        if (!slides?.length) {
            return;
        }

        const isSingle = slides.length === 1;

        Array.from(slides).forEach((slide, index) => {
            slide.classList.toggle(
                'swiper-slide-thumb-active',
                isSingle && index === 0
            );
        });
    }

    bindThumbnailHeightSync() {
        this.unbindThumbnailHeightSync();

        if (!this.root || this.context !== 'product') {
            return;
        }

        this.thumbnailHeightObserver = new ResizeObserver(
            this.onThumbnailHeightSyncBound
        );
        this.thumbnailHeightObserver.observe(this.root);

        window.addEventListener('resize', this.onThumbnailHeightSyncBound);
        this.syncSidebarThumbnailsHeight();
    }

    unbindThumbnailHeightSync() {
        this.thumbnailHeightObserver?.disconnect();
        this.thumbnailHeightObserver = null;
        window.removeEventListener('resize', this.onThumbnailHeightSyncBound);
    }

    destroy() {
        if (this.isLightboxOpen()) {
            this.closeLightbox();
        }

        this.unbindThumbnailHeightSync();
        this.unbindLightboxImageEvents();
        this.swiperInstance?.destroy(true, true);
        this.sidebarThumbnailsSwiperInstance?.destroy(true, true);
        this.lightboxThumbnailsSwiperInstance?.destroy(true, true);
        this.lightboxSwiperInstance?.destroy(true, true);

        this.swiperInstance = null;
        this.sidebarThumbnailsSwiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
    }

    rebuildGallery(images) {
        this.destroy();
        this.renderImages(images);
        this.initSidebarThumbnails();
        this.initLightbox();
        this.initSwiper();
        this.events();
        this.bindThumbnailHeightSync();
    }

    async setImages(images) {
        if (!Array.isArray(images)) {
            return;
        }

        this._imagesUpdateToken += 1;
        const updateToken = this._imagesUpdateToken;
        const rootParent = this.rootParent;

        if (images.length === 0) {
            this.rebuildGallery(images);
            rootParent?.toggleAttribute('hidden', true);

            if (rootParent) {
                rootParent.classList.remove('-is-updating');
                rootParent.style.opacity = '';
                rootParent.style.pointerEvents = '';
                rootParent.style.transition = '';
                rootParent.removeAttribute('aria-busy');
            }

            return;
        }

        rootParent?.removeAttribute('hidden');

        if (rootParent) {
            rootParent.classList.add('-is-updating');
            rootParent.style.pointerEvents = 'none';
            rootParent.setAttribute('aria-busy', 'true');
        }

        await fadeGalleryOpacity(rootParent, 0);

        if (updateToken !== this._imagesUpdateToken) {
            return;
        }

        await preloadGalleryImages(images);

        if (updateToken !== this._imagesUpdateToken) {
            return;
        }

        this.rebuildGallery(images);

        if (updateToken !== this._imagesUpdateToken) {
            return;
        }

        await fadeGalleryOpacity(rootParent, 1);

        if (updateToken !== this._imagesUpdateToken || !rootParent) {
            return;
        }

        rootParent.classList.remove('-is-updating');
        rootParent.style.pointerEvents = '';
        rootParent.removeAttribute('aria-busy');
    }

    toggleSidebarThumbnails(imageCount) {
        const hasThumbnails = imageCount >= 1;
        const hasMultipleImages = imageCount > 1;

        this.rootParent?.classList.toggle('has-thumbnails', hasThumbnails);
        this.rootParent?.classList.toggle(
            '[--product-gallery-thumb-offset:96px]',
            hasThumbnails
        );
        this.rootParent?.classList.toggle(
            '[--product-gallery-thumb-offset:0px]',
            !hasThumbnails
        );

        if (this.sidebarThumbnailsEl) {
            this.sidebarThumbnailsEl.classList.toggle('hidden', !hasThumbnails);
            this.sidebarThumbnailsEl.classList.toggle(
                'max-desktop:hidden',
                hasThumbnails
            );
            this.sidebarThumbnailsEl.classList.toggle(
                'desktop:block',
                hasThumbnails
            );
        }

        if (this.arrowsWrapper) {
            this.arrowsWrapper.classList.add('hidden', 'max-desktop:hidden');
            this.arrowsWrapper.classList.toggle('desktop:block', hasMultipleImages);
        }

        if (this.paginationEl) {
            this.paginationEl.classList.toggle('hidden', !hasMultipleImages);
            this.paginationEl.classList.toggle(
                'max-desktop:flex',
                hasMultipleImages
            );
            this.paginationEl.classList.toggle(
                'desktop:hidden',
                hasMultipleImages
            );
            this.paginationEl.toggleAttribute('hidden', !hasMultipleImages);
        }

        this.lightboxButton?.classList.toggle('hidden', imageCount === 0);

        this.syncThumbnailArrowsVisibility();
    }

    renderImages(images) {
        const slideClass = SLIDE_CLASSES[this.context] || SLIDE_CLASSES.default;
        const mainImageClass =
            MAIN_IMAGE_CLASSES[this.context] || MAIN_IMAGE_CLASSES.default;

        if (this.mainWrapper) {
            this.mainWrapper.replaceChildren(
                ...images.map(image =>
                    createSlide(image, slideClass, mainImageClass)
                )
            );
        }

        if (this.sidebarThumbnailsWrapper) {
            this.sidebarThumbnailsWrapper.replaceChildren(
                ...images.map((image, index) =>
                    createThumbSlide(
                        image,
                        SIDEBAR_THUMB_SLIDE_CLASSES,
                        images.length === 1 && index === 0
                    )
                )
            );
        }

        if (this.lightboxWrapper) {
            this.lightboxWrapper.replaceChildren(
                ...images.map(image =>
                    createSlide(
                        image,
                        'swiper-slide bg-background-secondary aspect-square group overflow-hidden isolate',
                        LIGHTBOX_IMAGE_CLASSES
                    )
                )
            );
        }

        if (this.thumbnailsWrapper) {
            this.thumbnailsWrapper.className = `swiper-wrapper ${
                images.length < 11 ? 'desktop:justify-center!' : ''
            }`;
            this.thumbnailsWrapper.replaceChildren(
                ...images.map(image =>
                    createThumbSlide(image, LIGHTBOX_THUMB_SLIDE_CLASSES)
                )
            );
        }

        this.toggleSidebarThumbnails(images.length);
        this.lightboxGalleryImgs =
            this.lightboxGallery?.querySelectorAll('img') || [];
    }

    initSidebarThumbnails() {
        const slideCount =
            this.sidebarThumbnailsWrapper?.children.length || 0;

        this.toggleSidebarThumbnails(slideCount);

        if (
            slideCount <= 1 ||
            !this.sidebarThumbnailsSwiperEl ||
            window.innerWidth < 1024
        ) {
            this.syncSingleThumbnailActiveState();
            this.syncSidebarThumbnailsHeight();
            this.syncThumbnailArrowsVisibility();
            return;
        }

        this.syncSidebarThumbnailsHeight();

        this.sidebarThumbnailsSwiperInstance = new Swiper(
            this.sidebarThumbnailsSwiperEl,
            {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: 10,
                freeMode: true,
                watchOverflow: true,
                watchSlidesProgress: true,
                modules: [Navigation],
                ...swiperProductGalleryThumbnailsNavigation(
                    this.sidebarThumbnailsArrowsEl
                ),
                on: {
                    init: () => this.syncThumbnailArrowsVisibility(),
                    resize: () => this.syncThumbnailArrowsVisibility(),
                    update: () => this.syncThumbnailArrowsVisibility(),
                },
            }
        );

        requestAnimationFrame(() => {
            this.syncSidebarThumbnailsHeight();
            this.syncThumbnailArrowsVisibility();
        });
    }

    initLightbox() {
        if (this.lighboxThumbnails) {
            this.lightboxThumbnailsSwiperInstance = new Swiper(
                this.lighboxThumbnails,
                {
                    breakpoints: {
                        0: {
                            slidesPerView: 2.5,
                        },
                        1024: {
                            slidesPerView: 'auto',
                        },
                    },
                }
            );
        }

        if (this.lightboxGallery) {
            const lightboxSlideCount =
                this.lightboxWrapper?.children.length || 0;
            const canRewind = lightboxSlideCount > 1;

            this.lightboxSwiperInstance = new Swiper(this.lightboxGallery, {
                slidesPerView: 1,
                speed: 1000,
                rewind: canRewind,
                modules: [Thumbs, Navigation],
                thumbs: {
                    swiper: this.lightboxThumbnailsSwiperInstance,
                },
                ...swiperNavigation(this.lightboxGalleryArrows),
            });

            this.lightboxSwiperInstance.on('slideChange', e => {
                this.swiperInstance?.slideTo(e.activeIndex);

                if (this.lightboxGalleryImgs) {
                    this.lightboxGalleryImgs.forEach(img => {
                        if (!img._zoomState) {
                            img._zoomState = {
                                scale: 1,
                                x: 0,
                                y: 0,
                                zoomed: false,
                            };
                        }
                        const state = img._zoomState;

                        if (state.zoomed) {
                            state.scale = 1;
                            state.x = 0;
                            state.y = 0;
                            state.zoomed = false;

                            this.applyTransform(img, state);
                            img.classList.remove('-zoommed');
                        }
                    });
                }
            });
        }
    }

    initSwiper() {
        const modules = [];
        const slideCount = this.mainWrapper?.children.length || 0;
        const canRewind = slideCount > 1;
        const args = {
            slidesPerView: 1,
            modules,
            speed: 700,
            rewind: canRewind,
        };

        if (this.paginationEl) {
            modules.push(Pagination);
            Object.assign(
                args,
                swiperProductGalleryPagination(this.paginationEl)
            );
        }

        if (this.sidebarThumbnailsSwiperInstance) {
            modules.push(Thumbs);
            args.thumbs = {
                swiper: this.sidebarThumbnailsSwiperInstance,
            };
        }

        if (this.arrowsEl && canRewind && window.innerWidth >= 1024) {
            modules.push(Navigation);
            Object.assign(args, swiperNavigation(this.arrowsEl));
        }

        this.swiperInstance = new Swiper(this.root, args);

        this.swiperInstance.on('slideChange', swiper => {
            this.lightboxSwiperInstance?.slideTo(swiper.activeIndex);
        });
    }

    events() {
        if (
            this.closeLightboxButton &&
            !this.closeLightboxButton.dataset.bound
        ) {
            this.closeLightboxButton.dataset.bound = '1';
            this.closeLightboxButton.addEventListener(
                'click',
                this.handleCloseLightbox.bind(this)
            );
        }

        if (this.lightboxButton && !this.lightboxButton.dataset.bound) {
            this.lightboxButton.dataset.bound = '1';
            this.lightboxButton.addEventListener(
                'click',
                this.handleOpenLightbox.bind(this)
            );
        }

        if (!this.keydownBound) {
            document.addEventListener('keydown', this.onLightboxKeydownsBound);
            this.keydownBound = true;
        }

        this.bindLightboxImageEvents();
    }

    bindLightboxImageEvents() {
        this.unbindLightboxImageEvents();

        this.lightboxGalleryImgs.forEach(img => {
            img.addEventListener('click', this.onLightboxImgClickBound);
        });
    }

    unbindLightboxImageEvents() {
        this.lightboxGalleryImgs.forEach(img => {
            img.removeEventListener('click', this.onLightboxImgClickBound);
        });
    }

    handleOpenLightbox() {
        this.openLightbox();
    }

    handleCloseLightbox() {
        this.closeLightbox();
    }

    onLightboxKeydowns(e) {
        if (!this.lighboxEl?.classList.contains(this.lightboxActiveClass)) {
            return;
        }

        const { key } = e;

        if (key === 'Escape') {
            this.handleCloseLightbox();
        } else if (key === 'ArrowLeft') {
            this.lightboxSwiperInstance?.slidePrev();
        } else if (key === 'ArrowRight') {
            this.lightboxSwiperInstance?.slideNext();
        }
    }

    onLightboxImgClick(e) {
        const img = e.currentTarget;

        if (!img._zoomState) {
            img._zoomState = {
                scale: 1,
                x: 0,
                y: 0,
                zoomed: false,
            };
        }

        const state = img._zoomState;

        if (state.zoomed) {
            state.scale = 1;
            state.x = 0;
            state.y = 0;
            state.zoomed = false;

            this.applyTransform(img, state);
            img.classList.remove('-zoommed');
            return;
        }

        const rect = img.getBoundingClientRect();

        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const newScale = 2.5;

        state.x = mx - (mx - state.x) * (newScale / state.scale);
        state.y = my - (my - state.y) * (newScale / state.scale);

        state.scale = newScale;
        state.zoomed = true;

        this.applyTransform(img, state);
        img.classList.add('-zoommed');
    }

    applyTransform(img, state) {
        img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    }

    mountLightboxToBody() {
        if (!this.lighboxEl || this.lighboxEl.parentElement === document.body) {
            return;
        }

        document.body.appendChild(this.lighboxEl);
    }

    isLightboxOpen() {
        return this.lighboxEl?.classList.contains(this.lightboxActiveClass);
    }

    openLightbox() {
        if (this.isLightboxOpen()) {
            return;
        }

        this.lighboxEl?.classList.add(this.lightboxActiveClass);
        Sidebar.lockPageScroll();
    }

    closeLightbox() {
        if (!this.isLightboxOpen()) {
            return;
        }

        this.lighboxEl?.classList.remove(this.lightboxActiveClass);
        Sidebar.unlockPageScroll();
    }
}

export const initProductGallery = el => {
    if (!el) {
        return null;
    }

    if (el._productGalleryInstance) {
        el._productGalleryInstance.destroy();
        el._productGalleryInstance = null;
    }

    const instance = new Gallery(el);
    el._productGalleryInstance = instance;

    return instance;
};

export const updateProductGallery = (root, images) => {
    if (!root || !Array.isArray(images)) {
        return null;
    }

    const instance = root._productGalleryInstance || initProductGallery(root);

    instance?.setImages(images);

    return instance;
};

window.mdaInitProductGallery = initProductGallery;
window.mdaUpdateProductGallery = updateProductGallery;

document
    .querySelectorAll('[data-context="product"] .js-gallery')
    .forEach(el => {
        initProductGallery(el);
    });
